import { pb } from './pocketbase';
import eventEmitter from './event.service';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Événements pour les utilisateurs
export const USER_EVENTS = {
    CREATED: 'userCreated',
    UPDATED: 'userUpdated',
    DELETED: 'userDeleted'
};

// Récupérer tous les utilisateurs avec les rôles autorisés (filtrés par les ACL PocketBase)
export const getUsers = async () => {
    try {
        // Vérifier l'authentification
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        const currentUser = pb.authStore.model;
        console.log('🔍 Utilisateur connecté:', {
            id: currentUser.id,
            email: currentUser.email,
            role: currentUser.role,
            partner_id: currentUser.partner_id
        });

        // Filtrer par les rôles autorisés (exclure les étudiants)
        const allowedRoles = ['Admin', 'Commercial', 'Pedagogical', 'Teacher'];
        const roleFilter = allowedRoles.map(role => `role ~ "${role}"`).join(' || ');

        const users = await pb.collection('users').getFullList({
            filter: `(${roleFilter})`,
            sort: '-created',
        });

        console.log('📊 Utilisateurs récupérés:', users.length);
        console.log('👥 Détails des utilisateurs:', users.map(u => ({
            id: u.id,
            email: u.email,
            role: u.role,
            partner_id: u.partner_id,
            status: u.status,
            actif: u.actif
        })));
        
        // Debug spécifique pour le statut
        users.forEach(user => {
            console.log(`🔍 User ${user.email}: status=${user.status} (${typeof user.status}), actif=${user.actif} (${typeof user.actif})`);
        });

        return users;
    } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        throw error;
    }
};

// Récupérer un utilisateur par son ID
export const getUser = async (userId) => {
    try {
        const user = await pb.collection('users').getOne(userId);
        return user;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur:', error);
        throw error;
    }
};

// Créer un nouvel utilisateur
export const createUser = async (userData) => {
    try {
        // Appeler l'API du serveur pour créer le membre
        const response = await fetch(`${API_BASE_URL}/api/createMember`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
            },
            body: JSON.stringify(userData) // Envoyer les données directement, sans les encapsuler
        });

        const responseData = await response.json();
        console.log('Réponse du serveur:', response.status, responseData);

        if (!response.ok) {
            // Vérifier si c'est une erreur d'email en double (409 Conflict)
            if (response.status === 409) {
                throw new Error('Un utilisateur avec cette adresse email existe déjà.');
            }
            // Si le backend renvoie un message d'erreur spécifique, l'utiliser
            if (responseData && responseData.error) {
                throw new Error(responseData.error);
            }
            throw new Error('Une erreur inattendue est survenue lors de la création du membre.');
        }
        
        // Créer un objet membre complet pour l'événement
        const newMember = {
            id: responseData.user.id,
            user_id: responseData.user.id,
            ...userData,
            created: responseData.user.created || new Date().toISOString(),
            updated: responseData.user.updated || new Date().toISOString(),
            expand: {
                user_id: responseData.user
            }
        };
        
        // Émettre un événement avec le nouveau membre
        eventEmitter.dispatch(USER_EVENTS.CREATED, newMember);
        
        return responseData;
    } catch (error) {
        console.error('Erreur lors de la création du membre:', error);
        
        // Erreurs spécifiques
        if (error.message.includes('Failed to fetch')) {
            throw new Error('Erreur de connexion au serveur. Veuillez réessayer.');
        }
        
        // Erreurs liées à l'email en double
        if (error.message.includes('email existe déjà') || 
            error.message.includes('email already exists') || 
            error.message.includes('Un utilisateur avec cet email existe')) {
            throw new Error('Un utilisateur avec cette adresse email existe déjà.');
        }
        
        // Propager l'erreur telle quelle si elle a un message
        throw error;
    }
};

// Créer un nouvel utilisateur
export const createMember = async (userData) => {
    console.log('[member.service] createMember appelé avec:', userData);
    try {
        // Appeler l'API du serveur pour créer le membre
        const response = await fetch(`${API_BASE_URL}/api/createMember`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
            },
            body: JSON.stringify(userData) // Envoyer les données directement, sans les encapsuler
        });

        const responseData = await response.json();
        console.log('Réponse du serveur:', response.status, responseData);
        console.log('Structure responseData complète:', JSON.stringify(responseData, null, 2));

        if (!response.ok) {
            // Vérifier si c'est une erreur d'email en double (409 Conflict)
            if (response.status === 409) {
                throw new Error('Un utilisateur avec cette adresse email existe déjà.');
            }
            // Si le backend renvoie un message d'erreur spécifique, l'utiliser
            if (responseData && responseData.error) {
                throw new Error(responseData.error);
            }
            throw new Error('Une erreur inattendue est survenue lors de la création du membre.');
        }
        
        // Vérifier que responseData.user existe
        if (!responseData || !responseData.user) {
            console.error('Réponse du serveur invalide:', responseData);
            throw new Error('Réponse du serveur invalide - utilisateur non créé');
        }
        
        // Créer un objet membre complet pour l'événement
        const newMember = {
            id: responseData.user.id,
            user_id: responseData.user.id,
            ...userData,
            created: responseData.user.created || new Date().toISOString(),
            updated: responseData.user.updated || new Date().toISOString(),
            expand: {
                user_id: responseData.user
            }
        };
        
        // Émettre un événement avec le nouveau membre
        eventEmitter.dispatch(USER_EVENTS.CREATED, newMember);
        
        return responseData;
    } catch (error) {
    
        // Erreurs liées à l'email en double
        if (error.message.includes('email existe déjà') || 
            error.message.includes('email already exists') || 
            error.message.includes('Un utilisateur avec cet email existe')) {
            throw new Error('Un utilisateur avec cette adresse email existe déjà.');
        }
        
        // Propager l'erreur telle quelle si elle a un message
        throw error;
    }
};

// Mettre à jour un utilisateur
export const updateUser = async (userId, userData) => {
    try {
        console.log('🔧 updateUser appelé:', { userId, userData });
        
        // Préparer les données à mettre à jour
        const userToUpdate = {
            ...userData
        };

        // Nettoyer les valeurs undefined
        Object.keys(userToUpdate).forEach(key => {
            if (userToUpdate[key] === undefined) {
                delete userToUpdate[key];
            }
        });

        console.log('📝 Données à envoyer à PocketBase:', userToUpdate);
        const updatedUser = await pb.collection('users').update(userId, userToUpdate);
        console.log('✅ Utilisateur mis à jour:', updatedUser);

        // Émettre l'événement de mise à jour
        eventEmitter.dispatch(USER_EVENTS.UPDATED, updatedUser);

        return updatedUser;
    } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'utilisateur:', error);
        throw error;
    }
};

// Supprimer un utilisateur
export const deleteUser = async (userId) => {
    try {
        // Vérifier l'authentification
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        await pb.collection('users').delete(userId);

        eventEmitter.dispatch(USER_EVENTS.DELETED, userId);

        return true;
    } catch (error) {
        console.error('Erreur lors de la suppression de l\'utilisateur:', error);
        throw error;
    }
};

// Récupérer les équipes pédagogiques (utilisateurs avec rôle Pedagogical)
export const getPedagogical = async () => {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        // Les ACL filtrent automatiquement par partner_id
        const users = await pb.collection('users').getFullList({
            filter: `role ~ "Pedagogical"`,
            sort: 'first_name,last_name',
        });

        console.log('📚 Équipes pédagogiques récupérées:', users.length);
        return users;
    } catch (error) {
        console.error('Erreur lors de la récupération des équipes pédagogiques:', error);
        throw error;
    }
};

// Récupérer les enseignants (utilisateurs avec rôle Teacher)
export const getTeachers = async () => {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        // Les ACL filtrent automatiquement par partner_id
        const users = await pb.collection('users').getFullList({
            filter: `role ~ "Teacher"`,
            sort: 'first_name,last_name',
        });

        console.log('👨‍🏫 Enseignants récupérés:', users.length);
        return users;
    } catch (error) {
        console.error('Erreur lors de la récupération des enseignants:', error);
        throw error;
    }
};

export const getCommercial = async () => {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        // Les ACL filtrent automatiquement par partner_id
        const users = await pb.collection('users').getFullList({
            filter: `role ~ "Commercial"`,
            sort: 'first_name,last_name',
        });

        console.log('💼 Commerciaux récupérés:', users.length);
        return users;
    } catch (error) {
        console.error('Erreur lors de la récupération des commerciaux:', error);
        throw error;
    }
};

export const getAdmin = async () => {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        // Les ACL filtrent automatiquement par partner_id
        const users = await pb.collection('users').getFullList({
            filter: `role ~ "Admin"`,
            sort: 'first_name,last_name',
        });

        console.log('👨‍💼 Administrateurs récupérés:', users.length);
        return users;
    } catch (error) {
        console.error('Erreur lors de la récupération des administrateurs:', error);
        throw error;
    }
};

// Rechercher des enseignants par nom/prénom
export const searchTeachers = async (searchQuery) => {
    try {
        if (!pb.authStore.isValid) {
            throw new Error('Utilisateur non authentifié');
        }

        if (!searchQuery || searchQuery.length < 2) {
            return [];
        }

        // Séparer la recherche en mots pour une recherche multi-termes
        const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
        
        // Construire le filtre : chaque mot doit matcher first_name OU last_name
        const termFilters = searchTerms.map(term => `(first_name ~ "${term}" || last_name ~ "${term}")`);
        const searchFilter = termFilters.join(' && ');

        // Les ACL filtrent automatiquement par partner_id
        const users = await pb.collection('users').getFullList({
            filter: `role ~ "Teacher" && ${searchFilter}`,
            sort: 'first_name,last_name',
        });

        return users;
    } catch (error) {
        console.error('Erreur lors de la recherche des enseignants:', error);
        throw error;
    }
};

export const getUsersByPartner = async (partnerId) => {
    try {
        if (!partnerId) {
            throw new Error('Partner ID requis');
        }

        // Filtrer par partner_id et par les rôles autorisés
        const allowedRoles = ['Admin', 'Commercial', 'Pedagogical', 'Teacher'];
        const roleFilter = allowedRoles.map(role => `role ~ "${role}"`).join(' || ');

        const users = await pb.collection('users').getFullList({
            filter: `partner_id = "${partnerId}" && (${roleFilter})`,
            sort: 'first_name,last_name',
            expand: 'user_id',
        });

        console.log(`👥 Utilisateurs récupérés pour partner ${partnerId}:`, users.length);
        console.log('🎭 Rôles trouvés:', users.map(u => u.role).flat());

        return users;
    } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs du partenaire:', error);
        throw error;
    }
};
